import axios, { AxiosError } from 'axios';
import { OrderSchemaType, ItemOrderSchemaType } from './order.schema';
import { SHOW_LEGACY_SECTIONS } from './constants';
import { ApiItems, Client, Seller, Resource } from './types';



interface Supplier {
  id: number;
  name: string;
  cnpj: string;
  contactName: string;
  email: string;
  phone: string;
}

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`;

export const fetchSuppliers = async (): Promise<{ id: number; name: string; cnpj: string; contactName: string; email: string; phone: string; }[]> => {
  const { data } = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, {
    params: { companyType: 'Fornecedor' }
  });

  return (data || []).map((c: any) => ({
    id: c.id,
    name: c.companyName || c.name,
    cnpj: c.cnpj || '',
    contactName: c.contactName || '',
    email: c.email || '',
    phone: c.phone || ''
  }));
};

export const fetchClients = async (): Promise<Client[]> => {
  const { data } = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, {
    params: { companyType: 'Cliente' }
  });

  console.log('📡 Resposta do GET de Clientes no banco:', data);

  const dataArray = Array.isArray(data) ? data : (data?.data || []);
  return dataArray.map((c: any) => ({ id: c.id, name: c.companyName || c.name }));
};

export const fetchSellers = async (): Promise<Seller[]> => {
  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/sellers`);
  return data;
};

export const fetchScripts = async (): Promise<string[]> => {
  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/scripts`);
  return data;
};

const transformOrderDataForBackend = (orderData: OrderSchemaType): any => {
  const processItem = (item: ItemOrderSchemaType, isSubItem = false) => {
    const hasSubItems = item.subItems && Array.isArray(item.subItems) && item.subItems.length > 0 &&
      item.subItems.some((subItem: ItemOrderSchemaType) => subItem.isSaved);

    const itemType =
      item.itemType === 'MATERIAL' ? 'MATERIAL' :
        item.itemType === 'PRODUCT' ? 'PRODUCT' :
          item.itemType === 'RESOURCE' ? 'RESOURCE' :
            'SERVICE';

    let savedSubItems: any[] = [];
    if (hasSubItems) {
      savedSubItems = item.subItems
        .filter((subItem: ItemOrderSchemaType) => subItem.isSaved)
        .map((subItem: ItemOrderSchemaType) => processItem(subItem, true));
    }

    let requestedProduct: any = {};

    if (SHOW_LEGACY_SECTIONS && item.itemType === 'MATERIAL' && item.feedstock) {
      requestedProduct = {
        ncm: item.feedstock.ncm || '',
        specs: item.feedstock.specs || '',
      };
    }

    if (item.feedstock && item.feedstock.quantidadePecas !== undefined && item.feedstock.quantidadePecas !== null) {
      requestedProduct.quantityPieces = item.feedstock.quantidadePecas;
    }

    // Persistir priceDetails dentro de requestedProduct para recuperar subtotal/unitPrice/prazo no futuro
    if (item.priceDetails) {
      requestedProduct.priceDetails = item.priceDetails;
    }

    let offeredProduct = {};
    if (SHOW_LEGACY_SECTIONS && item.itemType === 'MATERIAL' && item.offeredProductMaterial) {
      offeredProduct = {
        product: item.offeredProductMaterial.product || '',
        format: item.offeredProductMaterial.format || '',
        diameter: item.offeredProductMaterial.diameter || 0,
        thickness: item.offeredProductMaterial.thickness || 0,
        length: item.offeredProductMaterial.length || 0,
        finishing: item.offeredProductMaterial.finishing || '',
        weigth: item.offeredProductMaterial.weigth || '',
        quantity: item.offeredProductMaterial.quantity || 0,
        ncm: item.offeredProductMaterial.ncm || '',
        specs: item.offeredProductMaterial.specs || '',
      };
    }

    const heatTreatments = SHOW_LEGACY_SECTIONS ? {
      tensionRelief: item.heatTreatments?.tensionRelief || false,
      performance: item.heatTreatments?.performance || false,
      aging: item.heatTreatments?.aging || false,
      normalizationAndTempering: item.heatTreatments?.normalizationAndTempering || false,
      annealing: item.heatTreatments?.annealing || false,
      solutionTreatment: item.heatTreatments?.solutionTreatment || false,
      quenchingAndTempering: item.heatTreatments?.quenchingAndTempering || false,
    } : {};

    let specificId = {};

    if (itemType === 'MATERIAL') {
      if (item.materialId !== null && item.materialId !== undefined) specificId = { materialId: item.materialId };
    } else if (itemType === 'PRODUCT') {
      if (item.productId !== null && item.productId !== undefined) specificId = { productId: item.productId };
    } else if (itemType === 'RESOURCE') {
      if (item.resourceId !== null && item.resourceId !== undefined) specificId = { resourceId: item.resourceId };
    } else if (itemType === 'SERVICE') {
      if ((item as any).stageId !== null && (item as any).stageId !== undefined) specificId = { stageId: (item as any).stageId };
    }

    const transformedItem: any = {
      itemType,
      priceKgMin: item.priceDetails?.pricePerKgMin || 0,
      priceKg: item.priceDetails?.pricePerKgFinal || item.priceDetails?.unitPrice || 0,
      descount: item.priceDetails?.discount || 0,
      time: item.priceDetails?.deliveryDays || '',
      isPrincipal: item.isPrincipal || false,
      extraInfo: item.feedstock?.observacoes || item.finalProduct?.observacoes || item.resource?.observacoes || item.stage?.observacoes || '',
      quantity: typeof item.quantity === 'number' ? item.quantity : 0,
      sellingPrice: item.priceDetails?.sellingPrice !== undefined && item.priceDetails?.sellingPrice !== null ? Number(item.priceDetails.sellingPrice) : null,
      ...specificId
    };

    if (SHOW_LEGACY_SECTIONS) {
      transformedItem.requestedProduct = requestedProduct;
      transformedItem.offeredProduct = offeredProduct;
      transformedItem.heatTreatments = heatTreatments;
      transformedItem.deadlines = item.deadlines || {};
    } else {
      if (Object.keys(requestedProduct).length > 0) {
        transformedItem.requestedProduct = requestedProduct;
      }
    }

    if ((item as any).stageId) transformedItem.stageId = (item as any).stageId;
    if ((item as any).serviceCategoryId) transformedItem.serviceCategoryId = (item as any).serviceCategoryId;
    if ((item as any).serviceTypeId) transformedItem.serviceTypeId = (item as any).serviceTypeId;
    if ((item as any).resourceCategoryId) transformedItem.resourceCategoryId = (item as any).resourceCategoryId;
    if ((item as any).resourceTypeId) transformedItem.resourceTypeId = (item as any).resourceTypeId;
    if ((item as any).serviceSupplierCompanyId) transformedItem.serviceSupplierCompanyId = (item as any).serviceSupplierCompanyId;
    if ((item as any).resourceSupplierCompanyId) transformedItem.resourceSupplierCompanyId = (item as any).resourceSupplierCompanyId;
    if ((item as any).scriptId) transformedItem.scriptId = (item as any).scriptId;
    if ((item as any).stockId) transformedItem.stockId = (item as any).stockId;

    // Produto engenheirado (PRD 5): nome customizado por pedido + NCM espelhado do produto.
    if (itemType === 'PRODUCT') {
      const nomeCustomizado = item.finalProduct?.nomeCustomizado;
      if (nomeCustomizado && String(nomeCustomizado).trim()) {
        transformedItem.nomeCustomizado = String(nomeCustomizado).trim();
      }
    }
    // NCM se aplica a qualquer itemType (PRODUCT, MATERIAL, etc.)
    if ((item as any).ncmId !== undefined && (item as any).ncmId !== null) {
      transformedItem.ncmId = (item as any).ncmId;
    }

    if (item.parentId) transformedItem.parentId = item.parentId;

    if (savedSubItems.length > 0) {
      transformedItem.subItems = savedSubItems;
      
      if (SHOW_LEGACY_SECTIONS && item.itemType === 'MATERIAL') {
        transformedItem.requestedProduct = { ...transformedItem.requestedProduct, product: 'Container' };
        transformedItem.offeredProduct = { ...transformedItem.offeredProduct, product: 'Container' };
      }
    }

    return transformedItem;
  };

  const transformedItems = orderData.items
    .filter(item => item.isSaved)
    .map(item => processItem(item));

  const validateItemIds = (items: any[]) => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      let idCount = 0;
      if (item.materialId !== undefined && item.materialId !== null) idCount++;
      if (item.productId !== undefined && item.productId !== null) idCount++;
      if (item.resourceId !== undefined && item.resourceId !== null) idCount++;

      if (item.subItems && Array.isArray(item.subItems) && item.subItems.length > 0) {
        validateItemIds(item.subItems);
      }
    }
  };

  validateItemIds(transformedItems);

  const countSubItems = (items: any[]) => {
    return items.reduce((count, item) => {
      if (item.subItems && Array.isArray(item.subItems)) {
        return count + item.subItems.length + countSubItems(item.subItems);
      }
      return count;
    }, 0);
  };

  const totalSubItems = countSubItems(transformedItems);

  const output = {
    supplierId: orderData.supplierId || null,
    clientId: orderData.clientId || null,
    sellerId: orderData.sellerId || null,
    orderType: orderData.orderType || 'Standard',
    orderTypeId: orderData.orderTypeId || null,
    orderDate: orderData.orderDate || new Date().toISOString(),
    deliveryDate: orderData.deliveryDate || null,
    billingDate: orderData.billingDate || null,
    clientOrderNumber: orderData.clientOrderNumber ? Number(orderData.clientOrderNumber) : null,
    representativeId: orderData.representativeId || null,
    orderSituationId: orderData.orderSituationId || null,
    generalClassId: orderData.generalClassId || null,
    sellingPrice: orderData.sellingPrice !== undefined && orderData.sellingPrice !== null ? Number(orderData.sellingPrice) : null,
    paymentConditionId: orderData.paymentConditionId || null,
    shippingTypeId: orderData.shippingTypeId || null,
    shippingValue: orderData.shippingValue || 0,
    trackingCode: orderData.trackingCode || '',
    fileIds: orderData.fileIds || [],
    attachments: orderData.attachments || [],
    extraInfo: orderData.extraInfo || '',
    criticalAnalysis: orderData.criticalAnalysis || false,
    criticalAnalysisDesc: orderData.criticalAnalysisDesc || '',
    totalQuantity: orderData.totalQuantity || 0,
    taxes: orderData.taxes || 0,
    total: orderData.total || 0,
    // Parcelas/formas já vêm no shape do backend; só envia quando definidos.
    ...((orderData as any).installments !== undefined ? { installments: (orderData as any).installments } : {}),
    ...((orderData as any).paymentMethods !== undefined ? { paymentMethods: (orderData as any).paymentMethods } : {}),
    items: transformedItems
  };
  return output;
};

export const createSteelOrder = async (orderData: OrderSchemaType): Promise<any> => {
  try {
    if (!orderData.items || orderData.items.length === 0 || !orderData.items.some(item => item.isSaved)) {
      throw new Error('O pedido deve conter pelo menos um item salvo.');
    }

    const findSubItems = (items: ItemOrderSchemaType[]) => {
      let totalSubItems = 0;
      for (const item of items) {
        if (item.subItems && Array.isArray(item.subItems) && item.subItems.length > 0) {
          const savedSubItems = item.subItems.filter((si: ItemOrderSchemaType) => si.isSaved);
          totalSubItems += savedSubItems.length;
          totalSubItems += findSubItems(savedSubItems);
        }
      }
      return totalSubItems;
    };

    const itemsCount = orderData.items.filter(item => item.isSaved).length;
    const subItemsCount = findSubItems(orderData.items);

    const transformedData = transformOrderDataForBackend(orderData);

    if (!transformedData.orderDate) {
      transformedData.orderDate = new Date().toISOString();
    }
    if (!transformedData.items || transformedData.items.length === 0) {
      throw new Error('O pedido deve conter pelo menos um item.');
    }

    console.log('📦 PAYLOAD FINAL ENVIADO AO BACKEND (CREATE):', JSON.stringify(transformedData, null, 2));

    const { data } = await axios.post(
      `${API_BASE_URL}/order`,
      transformedData,
      { timeout: 30000 }
    );

    return data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const errorMessage = error.response.data?.message;
        if (Array.isArray(errorMessage)) {
          throw new Error(`Erros de validação do servidor:\n${errorMessage.join('\n')}`);
        } else if (errorMessage) {
          throw new Error(`Erro do servidor: ${errorMessage}`);
        } else {
          throw new Error(`Erro ${error.response.status}: ${JSON.stringify(error.response.data)}`);
        }
      } else if (error.request) {
        throw new Error('Sem resposta do servidor. Verifique sua conexão.');
      }
    }
    throw error instanceof Error ? error : new Error(String(error));
  }
};

export const fetchOrderById = async (orderId: string): Promise<OrderSchemaType> => {
  const { data } = await axios.get(`${API_BASE_URL}/order/${orderId}`);

  console.log("PAYLOAD DO PEDIDO:", data);

  const transformedData = {
    ...data,
    paymentConditionName: data.PaymentCondition?.name || undefined,
    shippingTypeName: data.ShippingType?.name || undefined,
    paymentMethodId: data.OrderPaymentMethod?.[0]?.paymentMethodId || undefined,
    paymentMethodName: data.OrderPaymentMethod?.[0]?.PaymentMethod?.name || undefined,
    paymentMethodDescription: data.OrderPaymentMethod?.[0]?.description || undefined,
    orderType: data.OrderType?.name ?? data.orderType?.name ?? (typeof data.orderType === 'string' ? data.orderType : ''),
    representativeName: data.Representative?.name ?? data.representative?.name ?? '',
    orderSituationName: data.OrderSituation?.name ?? data.orderSituation?.name ?? '',
    generalClassName: data.GeneralClass?.name ?? data.generalClass?.name ?? '',
    items: data.items?.map((item: any) => ({
      ...item,
      isSaved: true,
      itemType: item.itemType || 'MATERIAL',
      deadlines: SHOW_LEGACY_SECTIONS ? (item.deadlines || { internal: [], external: [] }) : { internal: [], external: [] },
      heatTreatments: SHOW_LEGACY_SECTIONS ? (item.heatTreatments || {
        tensionRelief: false,
        performance: false,
        aging: false,
        normalizationAndTempering: false,
        annealing: false,
        solutionTreatment: false,
        quenchingAndTempering: false,
      }) : {
        tensionRelief: false,
        performance: false,
        aging: false,
        normalizationAndTempering: false,
        annealing: false,
        solutionTreatment: false,
        quenchingAndTempering: false,
      },
      ...(SHOW_LEGACY_SECTIONS ? {
        requestedProductMaterial: item.itemType === 'MATERIAL' ? {
          ...item.requestedProduct,
          diameter: typeof item.requestedProduct?.diameter === 'number' ? item.requestedProduct.diameter : undefined,
          thickness: typeof item.requestedProduct?.thickness === 'number' ? item.requestedProduct.thickness : undefined,
          length: typeof item.requestedProduct?.length === 'number' ? item.requestedProduct.length : undefined,
          quantity: typeof item.requestedProduct?.quantity === 'number' ? item.requestedProduct.quantity : undefined,
          weigth: (item.requestedProduct?.weigth === 'Auto' || typeof item.requestedProduct?.weigth === 'number')
            ? item.requestedProduct.weigth
            : undefined
        } : undefined,
        offeredProductMaterial: item.itemType === 'MATERIAL' ? {
          ...item.offeredProduct,
          diameter: typeof item.offeredProduct?.diameter === 'number' ? item.offeredProduct.diameter : undefined,
          thickness: typeof item.offeredProduct?.thickness === 'number' ? item.offeredProduct.thickness : undefined,
          length: typeof item.offeredProduct?.length === 'number' ? item.offeredProduct.length : undefined,
          quantity: typeof item.offeredProduct?.quantity === 'number' ? item.offeredProduct.quantity : undefined,
          weigth: (item.offeredProduct?.weigth === 'Auto' || typeof item.offeredProduct?.weigth === 'number')
            ? item.offeredProduct.weigth
            : undefined
        } : undefined,
      } : {}),
      finalProduct: item.itemType === 'PRODUCT' ? {
        produto: (item.Product || item.product)?.name || '',
        codigo: (item.Product || item.product)?.productCode || '',
        nomeCustomizado: item.nomeCustomizado || '',
        quantidade: typeof item.quantity === 'number' ? item.quantity : 0,
        observacoes: item.extraInfo || '',
        script: (item.Script || item.script)?.name || ''
      } : undefined,
      resource: item.itemType === 'RESOURCE' ? {
        insumo: (item.Resource || item.resource)?.name || '',
        categoria: (item.ResourceCategory || item.resourceCategory)?.name || '',
        tipo: (item.ResourceType || item.resourceType)?.name || '',
        fornecedor: (item.ResourceSupplierCompany || item.resourceSupplierCompany)?.companyName || '',
        quantidade: typeof item.quantity === 'number' ? item.quantity : 0,
        observacoes: item.extraInfo || ''
      } : undefined,
      stage: item.itemType === 'SERVICE' ? {
        servico: (item.Stage || item.stage)?.name || '',
        categoria: (item.ServiceCategory || item.serviceCategory)?.name || '',
        tipo: (item.ServiceType || item.serviceType)?.name || '',
        fornecedor: (item.ServiceSupplierCompany || item.serviceSupplierCompany)?.companyName || '',
        quantidade: typeof item.quantity === 'number' ? item.quantity : 0,
        observacoes: item.extraInfo || ''
      } : undefined,
      feedstock: item.itemType === 'MATERIAL' ? {
        materiaPrima: item.Material?.name || '',
        quantidade: typeof item.quantity === 'number' ? item.quantity : 0,
        quantidadePecas: typeof item.requestedProduct?.quantityPieces === 'number' ? item.requestedProduct.quantityPieces : undefined,
        observacoes: item.extraInfo || '',
        stockDescription: item.Stock?.description || ''
      } : undefined,
      priceDetails: {
        pricePerKgMin: item.requestedProduct?.priceDetails?.pricePerKgMin ?? (typeof item.priceKgMin === 'number' ? item.priceKgMin : undefined),
        pricePerKgFinal: item.requestedProduct?.priceDetails?.pricePerKgFinal ?? (typeof item.priceKg === 'number' ? item.priceKg : undefined),
        discount: item.requestedProduct?.priceDetails?.discount ?? (typeof item.descount === 'number' ? item.descount : undefined),
        deliveryDays: item.requestedProduct?.priceDetails?.deliveryDays ?? (item.time || ''),
        unitPrice: item.requestedProduct?.priceDetails?.unitPrice ?? undefined,
        itemSubtotal: item.requestedProduct?.priceDetails?.itemSubtotal ?? undefined,
        itemTaxes: item.requestedProduct?.priceDetails?.itemTaxes ?? undefined
      },
      subItems: item.subItems?.map((subItem: any) => ({
        ...subItem,
        isSaved: true,
        itemType: subItem.itemType || 'MATERIAL',
        deadlines: SHOW_LEGACY_SECTIONS ? (subItem.deadlines || { internal: [], external: [] }) : { internal: [], external: [] },
        heatTreatments: SHOW_LEGACY_SECTIONS ? (subItem.heatTreatments || {
          tensionRelief: false,
          performance: false,
          aging: false,
          normalizationAndTempering: false,
          annealing: false,
          solutionTreatment: false,
          quenchingAndTempering: false,
        }) : {
          tensionRelief: false,
          performance: false,
          aging: false,
          normalizationAndTempering: false,
          annealing: false,
          solutionTreatment: false,
          quenchingAndTempering: false,
        },
        ...(SHOW_LEGACY_SECTIONS ? {
          requestedProductMaterial: subItem.itemType === 'MATERIAL' ? {
            ...subItem.requestedProduct,
            diameter: typeof subItem.requestedProduct?.diameter === 'number' ? subItem.requestedProduct.diameter : undefined,
            thickness: typeof subItem.requestedProduct?.thickness === 'number' ? subItem.requestedProduct.thickness : undefined,
            length: typeof subItem.requestedProduct?.length === 'number' ? subItem.requestedProduct.length : undefined,
            quantity: typeof subItem.quantity === 'number' ? subItem.quantity : (typeof subItem.requestedProduct?.quantity === 'number' ? subItem.requestedProduct.quantity : undefined),
            weigth: (subItem.requestedProduct?.weigth === 'Auto' || typeof subItem.requestedProduct?.weigth === 'number')
              ? subItem.requestedProduct.weigth
              : undefined
          } : undefined,
          offeredProductMaterial: subItem.itemType === 'MATERIAL' ? {
            ...subItem.offeredProduct,
            diameter: typeof subItem.offeredProduct?.diameter === 'number' ? subItem.offeredProduct.diameter : undefined,
            thickness: typeof subItem.offeredProduct?.thickness === 'number' ? subItem.offeredProduct.thickness : undefined,
            length: typeof subItem.offeredProduct?.length === 'number' ? subItem.offeredProduct.length : undefined,
            quantity: typeof subItem.quantity === 'number' ? subItem.quantity : (typeof subItem.offeredProduct?.quantity === 'number' ? subItem.offeredProduct.quantity : undefined),
            weigth: (subItem.offeredProduct?.weigth === 'Auto' || typeof subItem.offeredProduct?.weigth === 'number')
              ? subItem.offeredProduct.weigth
              : undefined
          } : undefined,
        } : {}),
        finalProduct: subItem.itemType === 'PRODUCT' ? {
          produto: (subItem.Product || subItem.product)?.name || '',
          quantidade: typeof subItem.quantity === 'number' ? subItem.quantity : 0,
          observacoes: subItem.extraInfo || '',
          script: (subItem.Script || subItem.script)?.name || ''
        } : undefined,
        resource: subItem.itemType === 'RESOURCE' ? {
          insumo: (subItem.Resource || subItem.resource)?.name || '',
          categoria: (subItem.ResourceCategory || subItem.resourceCategory)?.name || '',
          tipo: (subItem.ResourceType || subItem.resourceType)?.name || '',
          fornecedor: (subItem.ResourceSupplierCompany || subItem.resourceSupplierCompany)?.companyName || '',
          quantidade: typeof subItem.quantity === 'number' ? subItem.quantity : 0,
          observacoes: subItem.extraInfo || ''
        } : undefined,
        stage: subItem.itemType === 'SERVICE' ? {
          servico: (subItem.Stage || subItem.stage)?.name || '',
          categoria: (subItem.ServiceCategory || subItem.serviceCategory)?.name || '',
          tipo: (subItem.ServiceType || subItem.serviceType)?.name || '',
          fornecedor: (subItem.ServiceSupplierCompany || subItem.serviceSupplierCompany)?.companyName || '',
          quantidade: typeof subItem.quantity === 'number' ? subItem.quantity : 0,
          observacoes: subItem.extraInfo || ''
        } : undefined,
        feedstock: subItem.itemType === 'MATERIAL' ? {
          materiaPrima: (subItem.Material || subItem.material)?.name || '',
          quantidade: typeof subItem.quantity === 'number' ? subItem.quantity : 0,
          quantidadePecas: typeof subItem.requestedProduct?.quantityPieces === 'number' ? subItem.requestedProduct.quantityPieces : undefined,
          observacoes: subItem.extraInfo || ''
        } : undefined,
        priceDetails: {
          pricePerKgMin: subItem.requestedProduct?.priceDetails?.pricePerKgMin ?? (typeof subItem.priceKgMin === 'number' ? subItem.priceKgMin : undefined),
          pricePerKgFinal: subItem.requestedProduct?.priceDetails?.pricePerKgFinal ?? (typeof subItem.priceKg === 'number' ? subItem.priceKg : undefined),
          discount: subItem.requestedProduct?.priceDetails?.discount ?? (typeof subItem.descount === 'number' ? subItem.descount : undefined),
          deliveryDays: subItem.requestedProduct?.priceDetails?.deliveryDays ?? (subItem.time || ''),
          unitPrice: subItem.requestedProduct?.priceDetails?.unitPrice ?? (typeof subItem.priceKg === 'number' ? subItem.priceKg : undefined),
          itemSubtotal: subItem.requestedProduct?.priceDetails?.itemSubtotal ?? undefined,
          itemTaxes: subItem.requestedProduct?.priceDetails?.itemTaxes ?? undefined
        }
      })) || []
    })) || []
  };

  return transformedData;
};

export const updateSteelOrder = async (orderId: string, orderData: OrderSchemaType): Promise<OrderSchemaType> => {
  console.log('--- INICIANDO UPDATE DO PEDIDO ---', orderId);
  try {
    if (!orderData.items || orderData.items.length === 0 || !orderData.items.some(item => item.isSaved)) {
      throw new Error('O pedido deve conter pelo menos um item salvo.');
    }

    const transformedData = transformOrderDataForBackend(orderData);
    
    console.log('PAYLOAD SENDO ENVIADO PARA O BACKEND (UPDATE):', JSON.stringify(transformedData, null, 2));

    const { data } = await axios.patch(`${API_BASE_URL}/order/${orderId}`, transformedData);
    
    console.log('SUCESSO NO UPDATE:', data);

    return data;
  } catch (error) {
    console.error('ERRO AO ATUALIZAR PEDIDO:', error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('ERRO AXIOS (UPDATE):', error.response.data);
      const errorMessage = error.response.data?.message;
      if (Array.isArray(errorMessage)) {
        throw new Error(`Erro de validação: ${errorMessage.join(', ')}`);
      } else if (typeof errorMessage === 'string') {
        throw new Error(`Erro do servidor: ${errorMessage}`);
      }
    }
    throw error;
  }
};

export const fetchRawMaterials = async (limit: number = 10, offset: number = 0, search?: string) => {
  const params: any = { limit };
  if (search) params.search = search;
  const page = Math.floor(offset / limit) + 1;
  params.page = page;

  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/products`, { params });
  return data.products || data;
};

export const fetchResources = async (limit: number = 10, offset: number = 0, search?: string) => {
  const params: any = { limit };
  if (search) params.search = search;
  const page = Math.floor(offset / limit) + 1;
  params.page = page;

  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/resources`, { params });
  return data.resources || data;
};

export const fetchProducts = async (limit: number = 10, offset: number = 0, search?: string) => {
  const params: any = { limit };
  if (search) params.search = search;
  const page = Math.floor(offset / limit) + 1;
  params.page = page;

  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/products`, { params });
  return data.products || data;
};

export const fetchServices = async (): Promise<{ id: number; name: string; category: string; type: string; }[]> => {
  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/services`);
  return data.services || data;
};

export const fetchServicesInfinite = async (limit: number = 10, offset: number = 0, search?: string) => {
  const params: any = { limit };
  if (search) params.search = search;
  const page = Math.floor(offset / limit) + 1;
  params.page = page;

  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/services`, { params });
  return data.services || data;
};

export const fetchCompaniesByStage = async (stageId: number, limit: number = 10, offset: number = 0, search?: string) => {
  const params: any = { stageId, limit, offset };
  if (search) params.search = search;

  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/service-suppliers`, { params });
  return data.suppliers || data;
};

export const fetchScriptsByProductId = async (productId: number, limit: number = 10, offset: number = 0, search?: string) => {
  const params: any = { productId, limit, offset };
  if (search) params.search = search;

  const { data } = await axios.get(`${API_BASE_URL}/select-in-order/scripts`, { params });
  return data.scripts || data;
};